import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Layers, Server, ArrowLeftRight, ShieldCheck, Activity, Lock, Globe, Clock,
  Cpu, Eye, CheckCircle2, Cloud, Database, Brain, Code2, Webhook, FileJson, Shield,
  Network, HardDrive, BarChart3, Zap, Award
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import DataFlowDiagram from '@/components/platform/DataFlowDiagram';

/* Animated counter */
const Counter = ({ target, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let cur = 0;
    const step = target / 30;
    const t = setInterval(() => {
      cur += step;
      if (cur >= target) { setCount(target); clearInterval(t); } else setCount(Math.round(cur));
    }, 40);
    return () => clearInterval(t);
  }, [inView, target]);
  return <span ref={ref}>{count}{suffix}</span>;
};

const WHY_ONE_PLATFORM = [
  { icon: ArrowLeftRight, key: 'twoWay' },
  { icon: Activity, key: 'monitoring' },
  { icon: Eye, key: 'crossChecking' },
  { icon: Layers, key: 'compounding' },
];

const PlatformPage = () => {
  const { t } = useTranslation();
  const [hoveredBenefit, setHoveredBenefit] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.getElementById(location.hash.slice(1));
        el?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [location.hash]);

  return (
    <PageLayout>
      {/* Hero */}
      <section className="pb-16 pt-8" data-testid="platform-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-teal/10 text-taxera-teal text-sm font-medium rounded-full mb-6">
              <Layers className="w-4 h-4" strokeWidth={1.5} />
              {t('platform.badge')}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-tight mb-6">
              {t('platform.heroTitle1')} <br />
              <span className="taxera-gradient-text">{t('platform.heroTitle2')}</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
              A cloud-native compliance platform combining embedded middleware, real-time APIs, AI-powered automation, and enterprise-grade security — purpose-built for global tax operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Data Flow Diagram */}
      <section id="how-it-works" className="pb-20" data-testid="platform-flow">
        <div className="max-w-5xl mx-auto px-6">
          <DataFlowDiagram />
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* CLOUD ARCHITECTURE */}
      {/* ═══════════════════════════════════════════ */}
      <section id="cloud" className="py-20 bg-slate-50" data-testid="platform-cloud">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-taxera-blue/10 text-taxera-blue text-xs font-bold uppercase tracking-wide rounded-full mb-4">
              <Cloud className="w-3.5 h-3.5" /> Cloud Architecture
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
              Built for enterprise scale and resilience
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Taxera runs on cloud-native infrastructure designed for the performance, availability, and data sovereignty requirements of multinational enterprises.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: Globe, title: 'Multi-Region EU Hosting', desc: 'Primary infrastructure hosted within the European Union, with configurable data residency to meet GDPR and local regulatory requirements. No cross-border data transfers without proper safeguards.' },
              { icon: Network, title: 'High Availability Architecture', desc: 'Active-active redundancy across availability zones. Automatic failover, load balancing, and horizontal scaling ensure the platform handles peak transaction volumes without degradation.' },
              { icon: HardDrive, title: 'Elastic Processing', desc: 'Auto-scaling compute and storage that adapts to your transaction volumes. Whether you process 10,000 or 10 million invoices per month, the platform scales seamlessly.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  <Icon className="w-6 h-6 text-taxera-blue mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-white rounded-2xl border border-slate-200 p-6">
            {[
              { value: '99.99%', label: 'Platform uptime' },
              { value: 'EU', label: 'Data residency' },
              { value: '<200ms', label: 'API response time' },
              { value: '24/7', label: 'Monitoring & alerting' },
            ].map((stat, i) => (
              <div key={stat.label} className="text-center">
                <div className="font-heading text-2xl font-bold text-taxera-blue">{stat.value}</div>
                <div className="text-xs text-slate-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* MIDDLEWARE — The Differentiator */}
      {/* ═══════════════════════════════════════════ */}
      <section id="integration" className="py-20" data-testid="platform-e2e">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-taxera-teal/10 text-taxera-teal text-xs font-bold uppercase tracking-wide rounded-full mb-4">
              <Layers className="w-3.5 h-3.5" /> {t('platform.middlewareBadge')}
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4 leading-tight">
              {t('platform.middlewareTitle')}
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Most compliance vendors hand you an API and wish you luck. Taxera's embedded middleware operates <strong>inside</strong> your ERP business process layer — extracting data natively, writing back acknowledgments, and monitoring every transaction in real-time.
            </p>
          </div>

          <div className="bg-slate-50 rounded-3xl p-8 md:p-12 border border-slate-200 mb-10">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-6">
                  We own the pipe. Both directions.
                </h3>
                <div className="space-y-4">
                  {[
                    { label: 'Native ERP Embedding', desc: 'Middleware lives inside your SAP, Oracle, or custom ERP business process — not alongside it.' },
                    { label: 'Two-Way Communication', desc: 'Data flows out, acknowledgments and status updates flow back. Your team sees everything in the ERP, not a separate portal.' },
                    { label: 'Tax Authority Connectivity', desc: 'Global coverage, direct portal submission, real-time response handling.' },
                    { label: 'One Integration, All Hubs', desc: 'Connect once — all five compliance hubs (e-Invoicing, eCompliance, eReporting, Tax Reporting, Tax Operations) work immediately.' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-taxera-teal shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="text-sm font-semibold text-taxera-charcoal">{item.label}</div>
                        <div className="text-xs text-slate-500">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: '1', label: 'Vendor', desc: 'for the complete chain' },
                  { value: '0', label: 'Handoffs', desc: 'between systems' },
                  { value: '2-way', label: 'Communication', desc: 'ERP <> Tax Authority' },
                  { value: 'Any', label: 'ERP Supported', desc: 'SAP & non-SAP' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                    <div className="font-heading text-3xl font-bold text-taxera-teal mb-1">{stat.value}</div>
                    <div className="text-xs font-semibold text-taxera-charcoal">{stat.label}</div>
                    <div className="text-2xs text-slate-400 mt-0.5">{stat.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-slate-900 rounded-2xl p-8 md:p-10">
            <h3 className="font-heading text-lg font-bold text-white mb-2">Why embedded middleware matters</h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-2xl">
              Very few vendors own this layer. Without it, you're integrating to an API — you build the connector, you maintain it, you troubleshoot it. With Taxera, the middleware is ours. We own the data extraction, the transformation, the submission, and the response handling.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { title: 'For IT', desc: 'One integration project instead of one per country, per mandate, per vendor. Middleware handles all format and protocol differences.' },
                { title: 'For Tax', desc: 'Real-time visibility into every transaction. Status updates in your ERP, not a separate portal. Full audit trail automatically.' },
                { title: 'For the Business', desc: 'Lower total cost of ownership. Fewer vendors. Faster rollouts to new countries. Compliance that scales with the business.' },
              ].map((item) => (
                <div key={item.title}>
                  <div className="text-sm font-bold text-taxera-teal mb-1">{item.title}</div>
                  <p className="text-xs text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* DATA PLATFORM */}
      {/* ═══════════════════════════════════════════ */}
      <section id="data-platform" className="py-20 bg-slate-50" data-testid="platform-data">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-600 text-xs font-bold uppercase tracking-wide rounded-full mb-4">
              <Database className="w-3.5 h-3.5" /> Data Platform
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
              Real-time processing at enterprise scale
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Every transaction is validated, enriched, and routed in real-time. The data platform underpinning Taxera ensures accuracy, speed, and full traceability across every mandate.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Zap, title: 'Real-Time Processing Engine', desc: 'Transactions are processed as they occur — no batch windows, no overnight runs. e-Invoices are validated and submitted within seconds of creation in your ERP.', color: '#8b5cf6' },
              { icon: BarChart3, title: 'Analytics & Dashboards', desc: 'Unified compliance analytics across all entities, countries, and mandate types. Real-time status monitoring, exception tracking, and trend analysis from a single pane of glass.', color: '#8b5cf6' },
              { icon: Database, title: 'Centralised Document Store', desc: 'Every invoice, report, and tax authority response stored in a searchable, auditable repository. Configurable retention policies aligned with local legal requirements.', color: '#8b5cf6' },
              { icon: Activity, title: 'Transaction Monitoring', desc: 'Proactive alerting on submission failures, validation errors, and authority rejections. Escalation workflows ensure nothing falls through the cracks.', color: '#8b5cf6' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  style={{ borderLeftWidth: 3, borderLeftColor: item.color }}
                >
                  <Icon className="w-6 h-6 mb-4" style={{ color: item.color }} strokeWidth={1.5} />
                  <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* WHY ONE PLATFORM */}
      {/* ═══════════════════════════════════════════ */}
      <section id="why-one-platform" className="py-20" data-testid="platform-why">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
              Why one platform changes everything
            </h2>
            <div className="w-20 h-1 taxera-gradient mx-auto rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {WHY_ONE_PLATFORM.map((item, i) => {
              const Icon = item.icon;
              const isHovered = hoveredBenefit === i;
              return (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  onMouseEnter={() => setHoveredBenefit(i)}
                  onMouseLeave={() => setHoveredBenefit(null)}
                  className={`bg-white rounded-2xl border p-8 transition-all duration-300 ${
                    isHovered ? 'border-taxera-teal/30 shadow-lg -translate-y-1' : 'border-slate-200'
                  }`}
                  data-testid={`platform-benefit-${i}`}
                >
                  <Icon className={`w-7 h-7 mb-4 transition-colors ${isHovered ? 'text-taxera-blue' : 'text-taxera-teal'}`} strokeWidth={1.5} />
                  <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-2">{t(`platform.whyOnePlatform.${item.key}`)}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{t(`platform.whyOnePlatform.${item.key}Desc`)}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* API */}
      {/* ═══════════════════════════════════════════ */}
      <section id="api" className="py-20 bg-slate-50" data-testid="platform-api">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-600 text-xs font-bold uppercase tracking-wide rounded-full mb-4">
              <Code2 className="w-3.5 h-3.5" /> APIs & Integration
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
              Open APIs for when you need them
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              While our middleware handles the heavy lifting natively, Taxera also exposes a full suite of RESTful APIs for custom integrations, automation, and extending the platform into your broader tech ecosystem.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-10">
            {/* API capabilities */}
            <div className="space-y-4">
              {[
                { icon: FileJson, title: 'RESTful API Suite', desc: 'Full CRUD operations for documents, entities, mandates, and submissions. JSON payloads, OAuth 2.0 authentication, versioned endpoints.' },
                { icon: Webhook, title: 'Webhooks & Events', desc: 'Real-time event notifications for submission status changes, validation results, and authority responses. Push-based — no polling required.' },
                { icon: Code2, title: 'SDKs & Developer Portal', desc: 'Client libraries, Postman collections, sandbox environments, and comprehensive API documentation for your development team.' },
                { icon: Server, title: 'Bulk Operations', desc: 'Batch submission APIs for high-volume scenarios. Upload thousands of documents in a single call with async processing and status callbacks.' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="flex items-start gap-4 bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                    <Icon className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <h3 className="text-sm font-bold text-taxera-charcoal mb-1">{item.title}</h3>
                      <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Middleware vs API comparison */}
            <div className="space-y-6">
              <div className="bg-slate-900 rounded-2xl p-7">
                <div className="text-xs font-bold tracking-[0.15em] text-taxera-teal uppercase mb-4">Middleware + API</div>
                <h3 className="font-heading text-xl font-bold text-white mb-4">Best of both worlds</h3>
                <p className="text-sm text-slate-400 mb-5 leading-relaxed">
                  For ERP-based compliance, our middleware handles everything natively. For custom applications, non-ERP systems, or extending the platform — use our APIs. You choose the integration pattern that fits each use case.
                </p>
                <ul className="space-y-2.5 text-sm text-slate-300">
                  {[
                    'Middleware for SAP & ERP — zero-code integration',
                    'APIs for custom apps, portals & dashboards',
                    'Webhooks for real-time event-driven workflows',
                    'Sandbox environment for testing before go-live',
                    'Rate limiting and throttling for production stability',
                  ].map((point, i) => (
                    <li key={`with-${point.slice(0, 25)}`} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-taxera-teal shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="text-xs font-bold tracking-[0.15em] text-red-400 uppercase mb-3">Without Taxera</div>
                <ul className="space-y-2 text-sm text-slate-600">
                  {[
                    'You build the interface to their API',
                    'You extract and transform the data',
                    'Error handling is your problem',
                    'No visibility once data leaves your system',
                    'Every integration is a custom IT project',
                  ].map((point, i) => (
                    <li key={`without-${point.slice(0, 25)}`} className="flex items-start gap-2">
                      <span className="text-red-400 mt-0.5 shrink-0">&#x2717;</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* AI & AUTOMATION */}
      {/* ═══════════════════════════════════════════ */}
      <section id="ai" className="py-20" data-testid="platform-ai">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-600 text-xs font-bold uppercase tracking-wide rounded-full mb-4">
              <Brain className="w-3.5 h-3.5" /> AI & Automation
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
              Intelligence built into the compliance layer
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Taxera uses AI and machine learning not as a marketing buzzword, but as a core engine for reducing manual work, catching errors before they reach tax authorities, and keeping your mandate coverage current.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Eye, title: 'Anomaly Detection', desc: 'Machine learning models trained on millions of tax transactions identify outliers, duplicates, and suspicious patterns before submission — reducing rejection rates and audit exposure.', color: '#f59e0b' },
              { icon: Cpu, title: 'Automated Mandate Updates', desc: 'When tax authorities change formats, schemas, or validation rules, our platform updates automatically. No manual configuration, no emergency patches, no missed deadlines.', color: '#f59e0b' },
              { icon: FileJson, title: 'Intelligent Document Processing', desc: 'Automated extraction and classification of incoming invoices and documents. Smart matching of purchase orders, delivery notes, and invoice data across formats and languages.', color: '#f59e0b' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  style={{ borderLeftWidth: 3, borderLeftColor: item.color }}
                >
                  <Icon className="w-6 h-6 mb-4" style={{ color: item.color }} strokeWidth={1.5} />
                  <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* SECURITY */}
      {/* ═══════════════════════════════════════════ */}
      <section id="security" className="py-20 bg-slate-900" data-testid="platform-security">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 text-taxera-teal text-xs font-bold uppercase tracking-wide rounded-full mb-4">
              <Lock className="w-3.5 h-3.5" /> Security & Privacy
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
              Enterprise-grade security by design
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Tax data is sensitive data. Taxera is designed from the ground up with the security, privacy, and compliance controls that enterprise organisations require.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Lock, title: 'Encryption', desc: 'AES-256 encryption at rest, TLS 1.3 in transit. Customer-managed encryption keys available for regulated industries.' },
              { icon: Shield, title: 'GDPR Compliance', desc: 'Data processing agreements, privacy impact assessments, right to erasure, and data portability built into the platform.' },
              { icon: Eye, title: 'Access Controls', desc: 'Role-based access control, multi-factor authentication, SSO integration (SAML, OIDC), and granular permission management.' },
              { icon: Activity, title: 'Audit Logging', desc: 'Immutable audit trail of every action, every access, every change. Exportable for regulatory review and internal compliance.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-white/[0.05] border border-white/[0.08] rounded-2xl p-6"
                >
                  <Icon className="w-5 h-5 text-taxera-teal mb-3" strokeWidth={1.5} />
                  <h3 className="font-heading text-sm font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* CERTIFICATIONS & MEMBERSHIPS */}
      {/* ═══════════════════════════════════════════ */}
      <section id="certifications" className="py-20" data-testid="platform-certifications">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-taxera-teal/10 text-taxera-teal text-xs font-bold uppercase tracking-wide rounded-full mb-4">
              <Award className="w-3.5 h-3.5" /> Certifications & Memberships
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
              Trusted, certified, and connected
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Taxera holds the certifications and industry memberships that matter for enterprise tax compliance — ensuring our platform meets the highest standards of security, interoperability, and regulatory alignment.
            </p>
          </div>

          {/* Certifications */}
          <div className="mb-12">
            <h3 className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase mb-6 text-center">Certifications</h3>
            <div className="flex justify-center">
              <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-lg w-full text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 rounded-2xl bg-taxera-blue/10 flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-8 h-8 text-taxera-blue" strokeWidth={1.5} />
                </div>
                <h4 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">ISO 27001</h4>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Internationally recognised information security management standard. Demonstrates that Taxera has established and maintains a systematic approach to managing sensitive company and customer information, ensuring it remains secure.
                </p>
              </div>
            </div>
          </div>

          {/* Memberships */}
          <div>
            <h3 className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase mb-6 text-center">Industry Memberships</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  name: 'Peppol',
                  fullName: 'Pan-European Public Procurement Online',
                  desc: 'Certified Peppol Access Point and Service Provider. Enables our clients to send and receive e-Invoices across the Peppol network — the standard for B2G and increasingly B2B e-Invoicing across Europe and beyond.',
                  color: '#0066CC',
                  logo: '/logos/peppol_logo.png',
                  url: 'https://peppol.org/',
                },
                {
                  name: 'GENA',
                  fullName: 'Global Exchange Network Association',
                  desc: 'Member of GENA, the global industry body connecting e-Invoicing service providers. Ensures interoperability and compliance alignment across our network of tax authority connections worldwide.',
                  color: '#14A3B8',
                  logo: '/logos/gena_logo.svg',
                  url: 'https://www.gena.net/',
                },
                {
                  name: 'DBNA',
                  fullName: 'Digital Business Networks Alliance',
                  desc: 'Member of DBNA, driving the adoption of digital business networks and standardised electronic document exchange. Ensures Taxera stays at the forefront of evolving e-Invoicing and eReporting standards.',
                  color: '#10b981',
                  logo: '/logos/dbna.png',
                  url: 'https://dbnalliance.org/',
                },
              ].map((membership, i) => (
                <motion.a
                  key={membership.name}
                  href={membership.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg transition-shadow text-center block group"
                  data-testid={`membership-${membership.name.toLowerCase()}`}
                >
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 bg-slate-50 group-hover:bg-slate-100 transition-colors">
                    <img src={membership.logo} alt={membership.name} className="h-8 w-auto object-contain" />
                  </div>
                  <h4 className="font-heading text-base font-bold text-taxera-charcoal mb-1 group-hover:text-taxera-blue transition-colors">{membership.name}</h4>
                  <p className="text-2xs text-slate-400 mb-3">{membership.fullName}</p>
                  <p className="text-sm text-slate-500 leading-relaxed">{membership.desc}</p>
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50" data-testid="platform-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">See it work for your stack</h2>
          <p className="text-lg text-slate-600 mb-8">Your ERP. Your mandates. Our platform. Let's map it out.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/evaluate" className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105" data-testid="platform-evaluate-btn">
              Evaluate Your Stack <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/products" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-taxera-charcoal border border-slate-200 rounded-full font-semibold hover:bg-slate-50 transition-all" data-testid="platform-products-btn">
              Explore Our Products
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default PlatformPage;
